import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const AddressAutocomplete = ({ 
    value, 
    onChange, 
    onSelect, 
    placeholder = "Enter address (e.g., 123 Main St, Springfield)",
    className,
    ...props 
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);

    const RADAR_API_KEY = process.env.REACT_APP_RADAR_API_KEY;
    const isEnabled = RADAR_API_KEY && RADAR_API_KEY.length > 0;

    // Debounce search
    useEffect(() => {
        if (!isEnabled || !value || value.length < 3) {
            setSuggestions([]);
            return;
        }

        const timer = setTimeout(() => {
            fetchSuggestions(value);
        }, 300);

        return () => clearTimeout(timer);
    }, [value, isEnabled]);

    // Handle click outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                suggestionsRef.current && 
                !suggestionsRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSuggestions = async (query) => {
        if (!RADAR_API_KEY) return;

        setLoading(true);
        try {
            const response = await fetch(
                `https://api.radar.io/v1/search/autocomplete?query=${encodeURIComponent(query)}&country=US&layers=address`,
                {
                    headers: {
                        'Authorization': RADAR_API_KEY
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setSuggestions(data.addresses || []);
                setShowSuggestions(true);
            }
        } catch (error) {
            console.error('Radar.io autocomplete error:', error);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        onChange(newValue);
        setHighlightedIndex(-1);
    };

    const handleSelectSuggestion = (suggestion) => {
        // Standardized address format
        const standardizedAddress = {
            street_address: suggestion.addressLabel || suggestion.formattedAddress,
            city: suggestion.city || '',
            state: suggestion.stateCode || suggestion.state || '',
            zip_code: suggestion.postalCode || '',
            country: suggestion.country || 'US',
            latitude: suggestion.latitude,
            longitude: suggestion.longitude,
            full_address: suggestion.formattedAddress || 
                `${suggestion.addressLabel}, ${suggestion.city}, ${suggestion.stateCode} ${suggestion.postalCode}`
        };

        // Update input with formatted address
        onChange(standardizedAddress.street_address);
        
        // Callback with full standardized data
        if (onSelect) {
            onSelect(standardizedAddress);
        }

        setShowSuggestions(false);
        setSuggestions([]);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0) {
                    handleSelectSuggestion(suggestions[highlightedIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                break;
            default:
                break;
        }
    };

    return (
        <div className="relative w-full">
            <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder={placeholder}
                    className={cn("pl-10 h-12 bg-accent border-border text-base", className)}
                    autoComplete="off"
                    {...props}
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                )}
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-60 overflow-auto"
                >
                    {suggestions.map((suggestion, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={() => handleSelectSuggestion(suggestion)}
                            className={cn(
                                "w-full flex items-start gap-3 px-4 py-3 text-left transition-colors",
                                "hover:bg-accent focus:bg-accent focus:outline-none",
                                highlightedIndex === index && "bg-accent"
                            )}
                            data-testid={`suggestion-${index}`}
                        >
                            <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <p className="font-medium text-foreground truncate">
                                    {suggestion.addressLabel || suggestion.number} {suggestion.street}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">
                                    {suggestion.city}{suggestion.stateCode && `, ${suggestion.stateCode}`} {suggestion.postalCode}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Powered by Radar badge when enabled */}
            {isEnabled && showSuggestions && suggestions.length > 0 && (
                <div className="absolute right-2 bottom-[-20px] text-xs text-muted-foreground">
                    Powered by Radar
                </div>
            )}
        </div>
    );
};

export default AddressAutocomplete;
